import React, { Suspense } from 'react';
import { CopyIcon, FileDownIcon, RefreshCwIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

import ActionResultCard from '../ActionResultCard';
import { MarkdownContent } from './MarkdownContent';
import type { ChatMessage } from './types';
import { cleanMessageContent, formatMessageTimestamp } from './chatHelpers';

export { cleanMessageContent, formatMessageTimestamp } from './chatHelpers';

/**
 * Citations renderer is loaded on demand: it drags in the map context (ArcGIS)
 * and most of the app graph, which the drawer should not pay for until a
 * message actually carries annotations.
 */
const AnnotationRenderer = React.lazy(() => import('../AnnotationRenderer'));

export interface MessageBubbleProps {
  message: ChatMessage;
  index: number;
  /** Timestamp override for optimistic messages of a not-yet-saved chat. */
  timestamp?: string | Date;
  /** True for the live streaming assistant bubble (no actions yet). */
  streaming?: boolean;
  onRedo?: (index: number) => void;
  onCopy?: (message: ChatMessage) => void;
  onDownload?: (message: ChatMessage) => void;
  onFetchFileUrl: (
    fileId: string
  ) => Promise<{ url: string; fileName: string; mimetype: string }>;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  index,
  timestamp,
  streaming = false,
  onRedo,
  onCopy,
  onDownload,
  onFetchFileUrl,
}) => {
  const role = (message.role ?? '').toUpperCase();
  const isAssistant = role === 'ASSISTANT';
  const displayRole = isAssistant ? 'PRATUS AI' : role;
  const content = cleanMessageContent(message.content);
  const hasAnnotations =
    isAssistant &&
    Array.isArray(message.annotations) &&
    message.annotations.length > 0;

  return (
    <article
      id={`dpgt-message-${index}`}
      data-role={isAssistant ? 'assistant' : 'user'}
      className={cn(
        'tw:flex tw:flex-col tw:gap-1 tw:rounded-xl tw:px-3.5 tw:py-3',
        isAssistant
          ? 'tw:border tw:border-border tw:bg-card tw:text-card-foreground'
          : 'tw:ml-6 tw:bg-accent/60 tw:text-foreground'
      )}
      aria-busy={streaming || undefined}
    >
      <header className="tw:flex tw:items-center tw:gap-2">
        <strong className="tw:text-xs tw:uppercase tw:tracking-wide tw:text-muted-foreground">
          {displayRole}:
        </strong>
        <span className="tw:text-[11px] tw:text-muted-foreground">
          {formatMessageTimestamp(timestamp ?? message.timestamp)}
        </span>
        {!streaming && (
          <div className="tw:ml-auto tw:flex tw:items-center tw:gap-0.5">
            {isAssistant && onRedo && (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      aria-label="Redo this response"
                      onClick={() => onRedo(index)}
                    />
                  }
                >
                  <RefreshCwIcon />
                </TooltipTrigger>
                <TooltipContent className="dark">
                  Redo this response
                </TooltipContent>
              </Tooltip>
            )}
            {onCopy && (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      aria-label="Copy message to clipboard"
                      onClick={() => onCopy(message)}
                    />
                  }
                >
                  <CopyIcon />
                </TooltipTrigger>
                <TooltipContent className="dark">
                  Copy to clipboard
                </TooltipContent>
              </Tooltip>
            )}
            {onDownload && (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      aria-label="Download message as Word document"
                      onClick={() => onDownload(message)}
                    />
                  }
                >
                  <FileDownIcon />
                </TooltipTrigger>
                <TooltipContent className="dark">Download .docx</TooltipContent>
              </Tooltip>
            )}
          </div>
        )}
      </header>

      <div className="EventAIAnalysis-paragraph tw:min-w-0 tw:text-sm tw:leading-relaxed tw:[&_p:last-child]:mb-0">
        {hasAnnotations ? (
          <Suspense fallback={<MarkdownContent>{content}</MarkdownContent>}>
            <AnnotationRenderer
              content={content}
              annotations={message.annotations as never}
              onFetchFileUrl={onFetchFileUrl}
              messageIndex={index}
            />
          </Suspense>
        ) : (
          <MarkdownContent>{content}</MarkdownContent>
        )}
        {isAssistant && !streaming && (
          <ActionResultCard actions={message.actions} />
        )}
      </div>
    </article>
  );
};

export default MessageBubble;
